// Quest Log ("Diário do Aventureiro"): as quests clássicas do Tibia (instâncias só do grupo), requisitos, recompensas
// oficiais e os baús de "escolha um" das quests já concluídas.
// Visual de diário: pergaminho, tinta sépia e selos de cera. CSS em client/css-parts/quests.css (prefixo .qs-).
import { QUESTS, type QuestDef } from '../../../../shared/src/game/quests';
import { catalogNow } from '../items';
import { ItemImg } from './ItemSlot';
import { useState } from 'preact/hooks';

const name = (id: number) => catalogNow()?.get(id)?.name ?? `item ${id}`;
const n = (v: number) => v.toLocaleString('pt-BR');

/** "?" que abre sozinho na primeira visita (lembrado no navegador; se não der para guardar, abre sempre) */
function useHelp(key: string, auto: boolean): [boolean, () => void] {
  const seen = (() => { try { return localStorage.getItem(key) === '1'; } catch { return false; } })();
  const [v, setV] = useState<boolean | null>(null);
  const open = v ?? (auto && !seen);
  return [open, () => { setV(!open); try { localStorage.setItem(key, '1'); } catch { /* sem storage */ } }];
}

function Reward(props: { id: number; n: number; onClick?: () => void }) {
  return (
    <span class={'item-slot mini' + (props.onClick ? ' pickable' : '')} title={`${props.n > 1 ? props.n + 'x ' : ''}${name(props.id)}`}
      onClick={props.onClick} style={props.onClick ? { cursor: 'pointer' } : undefined}>
      <ItemImg typeId={props.id} count={props.n} />
    </span>
  );
}

/** Janela de resgate: recompensas fixas e, se houver, a escolha de uma. Tudo vai para a Noxara Store Box. */
export function QuestClaim(props: { id: string; onClaim: (idx: number) => void }) {
  const q = QUESTS[props.id];
  const [sel, setSel] = useState(-1);
  if (!q) return null;
  const mustPick = !!q.choose?.length && !q.random;
  return (
    <div class="quest-pick qs-claim">
      <div class="qs-claim-head">
        <span class="qs-claim-chest">🎁</span>
        <div><b>{q.name} concluída!</b><small>Os baús estão abertos. Tudo vai para a Noxara Store Box.</small></div>
      </div>
      {(q.all?.length || q.gold) ? <>
        <small class="qs-claim-lbl">Recompensas</small>
        <div class="qs-claim-row">
          {q.all?.map(([it, c], i) => <span key={i} class="quest-choice qs-choice fixed"><Reward id={it} n={c} /><small>{name(it)}</small></span>)}
          {q.gold ? <span class="qs-gold-tag">+ {n(q.gold)} gp no banco</span> : null}
        </div></> : null}
      {q.choose?.length && q.random ? <>
        <small class="qs-claim-lbl">🎲 Um destes itens será sorteado, como no Tibia</small>
        <div class="qs-claim-row">{q.choose.map(([it, c], i) => <span key={i} class="quest-choice qs-choice"><Reward id={it} n={c} /><small>{name(it)}</small></span>)}</div></> : null}
      {mustPick ? <>
        <small class="qs-claim-lbl">Escolha uma (só um baú pode ser aberto, como no Tibia)</small>
        <div class="qs-claim-row">{q.choose!.map(([it, c], i) => (
          <span key={i} class={'quest-choice qs-choice' + (sel === i ? ' on' : '')} onClick={() => setSel(i)}><Reward id={it} n={c} onClick={() => setSel(i)} /><small>{name(it)}</small></span>
        ))}</div></> : null}
      <div class="qs-claim-foot">
        {mustPick && sel < 0 ? <small class="qs-hint">Clique num baú para escolher.</small> : mustPick ? <small class="qs-hint ok">Escolhido: <b>{name(q.choose![sel]![0])}</b></small> : <span />}
        <button class="tb-button green" disabled={mustPick && sel < 0} onClick={() => props.onClaim(q.random ? 0 : sel)}>Resgatar para a Noxara Store Box</button>
      </div>
    </div>
  );
}

type Filter = 'all' | 'open' | 'done';

export function Quests(props: { st: { done: string[]; pick: string[] } | null; level: number; inCity: boolean; onEnter: (id: string) => void; onPick: (id: string, idx: number) => void }) {
  const [filter, setFilter] = useState<Filter>('all');
  const [help, toggleHelp] = useHelp('nx-help-quests', !!props.st && props.st.done.length === 0);
  if (!props.st) return <p class="muted">Carregando...</p>;
  const done = new Set(props.st.done);
  const list = Object.values(QUESTS).filter((q) => q.classic || q.id === 'ascension').sort((a, b) => a.level - b.level);
  const status = (q: QuestDef) => done.has(q.id) ? 'Concluída' : props.level < q.level ? `Level ${q.level}` : q.requires && !done.has(q.requires) ? `Conclua ${QUESTS[q.requires]?.name}` : '';
  const doneCount = list.filter((q) => done.has(q.id)).length;
  const openCount = list.filter((q) => !status(q)).length;
  const lockedCount = list.length - doneCount - openCount;
  const pct = list.length ? (doneCount / list.length) * 100 : 0;
  const shown = list.filter((q) => filter === 'all' ? true : filter === 'done' ? done.has(q.id) : !status(q));

  return (
    <div class="qs">
      <div class="qs-head">
        <div class="qs-book" title={`${doneCount} de ${list.length} quests concluídas`}>
          <span>📜</span>
          <i class="qs-wax">{doneCount}</i>
        </div>
        <div class="qs-head-txt">
          <div class="qs-title">Diário do Aventureiro
            <button class="qs-help" onClick={toggleHelp} title="Como funciona">?</button></div>
          <div class="qs-sub">As quests clássicas do Tibia: salas fechadas, baús lendários e uma única chance por herói.</div>
          <div class="qs-bar" title={`${doneCount} de ${list.length}`}>
            <i style={{ width: `${pct}%` }} />
            <span>{doneCount === list.length && list.length ? 'Diário completo!' : `${doneCount} de ${list.length} quests concluídas`}</span>
          </div>
        </div>
      </div>

      {help && (
        <div class="qs-steps">
          <div><i>🗺️</i><b>1. Escolha a quest</b><small>Na cidade, clique em “Entrar”. É uma instância só sua ou da sua party (levada pelo líder).</small></div>
          <div><i>⚔️</i><b>2. Limpe as salas</b><small>As salas não se ligam: ao limpar uma, você escolhe avançar para a próxima.</small></div>
          <div><i>🎁</i><b>3. Abra os baús</b><small>Ao limpar a última sala, as recompensas vão para a Noxara Store Box. Em algumas, você escolhe um baú.</small></div>
          <div><i>🔏</i><b>4. Uma vez só</b><small>Cada herói faz cada quest uma vez. Algumas pedem outra quest antes.</small></div>
        </div>
      )}

      <div class="qs-stats">
        <div class="qs-ok"><small>Disponíveis</small><b>{openCount}</b><span>prontas para entrar</span></div>
        <div class="qs-sealed"><small>Concluídas</small><b>{doneCount}</b><span>selos no diário</span></div>
        <div><small>Bloqueadas</small><b>{lockedCount}</b><span>level ou requisito</span></div>
        <div class={props.st.pick.length ? 'qs-chest on' : 'qs-chest'}><small>Baús a abrir</small><b>{props.st.pick.length}</b><span>{props.st.pick.length ? 'resgate abaixo!' : 'nenhum pendente'}</span></div>
      </div>

      {props.st.pick.map((id) => <QuestClaim key={'p' + id} id={id} onClaim={(idx) => props.onPick(id, idx)} />)}
      {!props.inCity && <p class="qs-warn">🔒 Você está numa hunt: as quests começam pela cidade.</p>}

      <div class="qs-tabs">
        {([['all', 'Todas', list.length], ['open', 'Disponíveis', openCount], ['done', 'Concluídas', doneCount]] as const).map(([k, label, c]) => (
          <button key={k} class={'qs-tab' + (filter === k ? ' on' : '')} onClick={() => setFilter(k)}>{label} <em>{c}</em></button>
        ))}
      </div>

      <div class="qs-list">
        {shown.map((q) => {
          const st = status(q);
          const isDone = done.has(q.id);
          const cls = isDone ? ' done' : st ? ' locked' : ' open';
          return (
            <div key={q.id} class={'qs-card' + cls}>
              <div class="qs-lv" title={`Level mínimo ${q.level}`}><small>LV</small><b>{q.level}</b></div>
              <div class="qs-body">
                <div class="qs-name">
                  <b>{q.name}</b>
                  <span class="qs-chip">{q.maxPlayers === 1 ? '👤 sozinho' : `👥 1 a ${q.maxPlayers} heróis`}</span>
                  {q.promotion ? <span class="qs-chip promo">⭐ promotion</span> : null}
                  {q.requires ? <span class={'qs-chip' + (done.has(q.requires) ? ' met' : '')} title="Quest exigida antes">↳ {QUESTS[q.requires]?.name}</span> : null}
                </div>
                <small class="qs-desc">{q.desc ?? q.intro[q.intro.length - 1]?.text}</small>
                <div class="qs-rewards">
                  {q.choose && <><small class="qs-rl">{q.random ? '🎲 Sorteia 1' : '⚖️ Escolha 1'}</small>{q.choose.map(([it, c], i) => <Reward key={'c' + i} id={it} n={c} />)}</>}
                  {q.all && <><small class="qs-rl">{q.choose ? '+ todos' : 'Recompensa'}</small>{q.all.map(([it, c], i) => <Reward key={'a' + i} id={it} n={c} />)}</>}
                  {q.gold ? <span class="qs-gold-tag">+ {n(q.gold)} gp</span> : null}
                  {q.id === 'ascension' && <small class="qs-asc">Segunda promotion (+1 em skills e magic level, +5% vida e mana).</small>}
                </div>
              </div>
              <div class="qs-act">
                {isDone ? <span class="qs-stamp" title="Concluída">✔<small>Concluída</small></span>
                  : st ? <span class="qs-lock"><i>🔒</i><small>{st}</small></span>
                  : <button class="tb-button green" disabled={!props.inCity} title={props.inCity ? '' : 'Volte para a cidade'} onClick={() => props.onEnter(q.id)}>Entrar</button>}
              </div>
            </div>
          );
        })}
        {!shown.length && <p class="qs-empty">{filter === 'done' ? 'Nenhuma quest concluída ainda. Seu primeiro selo espera por você!' : 'Nenhuma quest disponível agora: suba de level ou conclua as quests exigidas.'}</p>}
      </div>
    </div>
  );
}
